import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetCampaign } from '~/hooks/apis/accommodation/useCampaigns';
import useAppNavigation from '~/hooks/useAppNavigation';

import CampaignBreadcrumbs from './components/CampaignBreadcrumbs';
import CampaignForm from './components/CampaignForm';
import AccommodationCampaignRulesTab from './tabs/AccommodationCampaignRulesTab';

const TAB_VALUES = ['details', 'rules'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function AccommodationCampaignEditPage() {
  const { campaignId, tab } = useParams<{ campaignId: string; tab?: string }>();
  const { replace } = useAppNavigation();
  const { data: campaign, isLoading, error } = useGetCampaign({ campaignId });

  useEffect(() => {
    if (!tab && campaignId) {
      replace(`/accommodation/campaigns/${campaignId}/details`);
    }
  }, [tab, campaignId, replace]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Failed to load campaign: {error.message}</Alert>
      </Container>
    );
  }

  if (!campaign) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning">Campaign not found.</Alert>
      </Container>
    );
  }

  const activeTab = (TAB_VALUES.includes(tab as TabValue) ? tab : 'details') as TabValue;

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>Edit Campaign: {campaign.name}</title>
      </Helmet>

      <CampaignBreadcrumbs currentPage={`Edit Campaign: ${campaign.name}`} />
      <PageHeader title={`Edit Campaign: ${campaign.name}`} />

      <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
        <Tabs value={activeTab} variant="scrollable">
          <Tab value="details" label="Details" to={`/accommodation/campaigns/${campaignId}/details`} component={Link} />
          <Tab value="rules" label="Rules" to={`/accommodation/campaigns/${campaignId}/rules`} component={Link} />
        </Tabs>

        <Box borderTop={1} borderColor="divider" p={2}>
          {activeTab === 'details' && (
            // Keyed on the fields the form initialises from — after a mutation the campaign query
            // is invalidated and refetched, producing a new key that remounts the form with fresh data.
            <CampaignForm
              key={`${campaign.name}::${campaign.displayName ?? ''}::${campaign.subtitle ?? ''}::${campaign.status}::${campaign.propertyIds.join(',')}::${JSON.stringify(campaign.schedules)}::${campaign.marketedLos ?? ''}::${JSON.stringify(campaign.defaultOccupancy)}`}
              campaign={campaign}
            />
          )}
          {activeTab === 'rules' && <AccommodationCampaignRulesTab campaignId={campaign.id} />}
        </Box>
      </Box>
    </Container>
  );
}

export default AccommodationCampaignEditPage;
